import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de Formación. Módulo 11.
 *
 * Vive en la raíz de la feature y no dentro del catálogo porque **el detalle del
 * curso viene detrás** y usa el mismo `/courses/`, el mismo tipo `Curso` y las
 * mismas categorías. Escribirlo una vez es la mitad del trabajo de la opción 2.
 *
 * Ningún endpoint cambia de firma. El único que se movió fue
 * `POST /courses/{id}/assign`, retirado del router el 2026-09-17 y sustituido
 * por la familia `/assignments`; ninguna pantalla lo llamaba.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

/** Los cuatro valores que acepta `course_type`. */
export type TipoDeCurso = 'induccion' | 'curso' | 'webinar' | 'taller';
export type NivelDeCurso = 'basico' | 'intermedio' | 'avanzado';
export type EstadoEditorial = 'publicado' | 'borrador' | 'archivado';
/** El semáforo de una asignación formal, que calcula `_assignment_status`. */
export type EstadoDeAsignacion = 'vencido' | 'proximo' | 'al_dia';

/** Lo que devuelve el serializador de `courses.py` en el listado. */
export interface Curso {
  id: number;
  title: string;
  course_type: TipoDeCurso | string;
  description?: string | null;
  objective?: string | null;
  category_id?: number | null;
  /** Portada subida. Si no hay, se usa `cover_icon` sobre `cover_icon_color`. */
  cover_url?: string | null;
  cover_icon?: string | null;
  cover_icon_color?: string | null;
  is_mandatory: boolean;
  level?: NivelDeCurso | string | null;
  modality?: string | null;
  status?: EstadoEditorial | string | null;
  validity_months?: number | null;
  lesson_count: number;
  progress_pct: number;
  completed: boolean;
  enrolled: boolean;
  /** Asignación formal: quién y para cuándo. */
  assigned: boolean;
  assignment_mandatory: boolean;
  assignment_due?: string | null;
  assignment_status?: EstadoDeAsignacion | null;
}

export interface CategoriaDeCurso {
  id: number;
  name: string;
  color?: string | null;
  icon?: string | null;
  course_count?: number;
}

/** Lo que se manda al crear un curso. Todo en el CUERPO: hasta el 2026-09-03 la
 *  descripción y el objetivo viajaban en la URL y un texto largo se cortaba con
 *  un 414 del proxy, sin mensaje entendible. */
export interface CuerpoDeCurso {
  title: string;
  course_type: string;
  category_id: number | null;
  cover_icon: string;
  cover_icon_color: string;
  description: string | null;
  objective: string | null;
  level: string | null;
  modality: string | null;
  status: string;
  is_mandatory: boolean;
  validity_months: number | null;
}

/* ── Cumplimiento ──────────────────────────────────────────────────────── */

export interface CumplimientoDeCurso {
  title: string;
  completed: number;
  total: number;
  pct: number;
}

export interface CertificadoPorVencer {
  full_name: string;
  area: string;
  course_title: string;
  status: 'vigente' | 'por_vencer' | 'vencido';
  expires_at?: string | null;
}

export interface Cumplimiento {
  total_collaborators: number;
  mandatory_compliance: CumplimientoDeCurso[];
  cert_summary: { total: number; vigente: number; por_vencer: number; vencido: number };
  expiring: CertificadoPorVencer[];
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  todo: ['formacion'] as const,
  cursos: ['formacion', 'cursos'] as const,
  categorias: ['formacion', 'categorias'] as const,
  cumplimiento: ['formacion', 'cumplimiento'] as const,
};

/* ── Lectura ───────────────────────────────────────────────────────────── */

/** El catálogo pide 200 y filtra en el cliente, igual que el vanilla: son los
 *  cursos de una empresa, no un archivo histórico. El servidor tope es 200. */
export const TOPE_DEL_CATALOGO = 200;

export function useCursos() {
  return useQuery({
    queryKey: claves.cursos,
    queryFn: () => api.get<Curso[]>('/courses/', { limit: TOPE_DEL_CATALOGO }),
  });
}

/**
 * Las categorías.
 *
 * En el vanilla iban con un `.catch(() => [])` pegado: si fallaban, los filtros
 * simplemente no aparecían y no había forma de saber por qué. Aquí van en su
 * propia consulta, así que el catálogo se pinta igual y quien mire los filtros
 * ve el estado de verdad.
 */
export function useCategorias(habilitado = true) {
  return useQuery({
    queryKey: claves.categorias,
    queryFn: () => api.get<CategoriaDeCurso[]>('/courses/categories'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

export function useCumplimiento(habilitado: boolean) {
  return useQuery({
    queryKey: claves.cumplimiento,
    queryFn: () => api.get<Cumplimiento>('/certificates/compliance'),
    enabled: habilitado,
  });
}

/* ── Escritura ─────────────────────────────────────────────────────────── */

export function useCrearCurso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: CuerpoDeCurso) => api.post<Curso>('/courses/', cuerpo),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.cursos });
      void qc.invalidateQueries({ queryKey: claves.categorias });
    },
  });
}

export function useCrearCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; color: string }) =>
      api.post<CategoriaDeCurso>('/courses/categories', { ...v, icon: 'bi-folder' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.categorias }),
  });
}

export function useBorrarCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message?: string }>(`/courses/categories/${id}`),
    // Solo se borra una categoría sin cursos —el servidor lo exige—, así que el
    // catálogo no cambia: basta con la lista de categorías.
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.categorias }),
  });
}

/* ══ El detalle del curso · opción 2 ══════════════════════════════════════ */

export type TipoDeLeccion = 'youtube' | 'pdf';

/** Una lección tal como la devuelve `GET /courses/{id}`. */
export interface Leccion {
  id: number;
  title: string;
  order: number;
  description?: string | null;
  video_type: TipoDeLeccion | string;
  video_url?: string | null;
  duration_minutes?: number | null;
  /** Presignada y temporal: no se guarda, se pide con el curso. */
  pdf_url?: string | null;
  completed: boolean;
}

/** En qué ruta va este curso, y qué toca después. `_course_path_context`. */
export interface RutaDelCurso {
  path_id: number;
  title: string;
  sequential: boolean;
  is_mandatory: boolean;
  step: number;
  total: number;
  completed_count: number;
  required_count: number;
  next_course?: { course_id: number; title: string } | null;
}

export type EstadoDeCertificado = 'vigente' | 'por_vencer' | 'vencido';

export interface CursoDetalle extends Curso {
  lessons: Leccion[];
  paths: RutaDelCurso[];
  /** En el detalle SÍ viene calculado; en el listado llega `null`. */
  has_quiz: boolean | null;
  quiz_passed: boolean;
  has_certificate: boolean;
  cert_status?: EstadoDeCertificado | null;
  cert_expires_at?: string | null;
}

/** Una pregunta del quiz para quien lo presenta. **Nunca trae la respuesta**:
 *  `correct_key` no sale del servidor en `/quiz`, solo en `/quiz/manage`. */
export interface PreguntaDelQuiz {
  id: number;
  question_text: string;
  options: Array<{ key: string; text: string }>;
}

export interface ResultadoDelQuiz {
  score: number;
  passed: boolean;
  correct: number;
  total: number;
  /** El mínimo de verdad, el que aplica el servidor. El vanilla lo tenía
   *  escrito a mano en tres textos distintos de la pantalla. */
  passing_score: number;
  attempt: number;
  certificate_url?: string | null;
  detail: Array<{
    question_text: string;
    selected_key?: string | null;
    selected_text?: string | null;
    is_correct: boolean;
  }>;
}

export interface RespuestaDeCompletar {
  progress_pct: number;
  completed_lessons: number;
  total_lessons: number;
  lessons_done: boolean;
  has_quiz: boolean;
  certificate_issued: boolean;
}

export interface CertificadoPropio {
  course_id: number;
  download_url?: string | null;
}

/* ── Gestión ──────────────────────────────────────────────────────────── */

export type DestinoDeAsignacion = 'todos' | 'area' | 'rol' | 'cargo' | 'usuario';

export interface Asignacion {
  id: number;
  target_label: string;
  target_count: number;
  is_mandatory: boolean;
  due_date?: string | null;
  note?: string | null;
}

export interface CuerpoDeAsignacion {
  target_type: DestinoDeAsignacion;
  due_date: string | null;
  is_mandatory: boolean;
  note: string | null;
  notify: boolean;
  area_id?: number;
  target_role?: string;
  target_position?: string;
  user_id?: number;
}

export type EstadoEnElEquipo = 'vencido' | 'proximo' | 'al_dia' | 'completado';

export interface ProgresoDelEquipo {
  summary: { total: number; completados: number; en_progreso: number; sin_iniciar: number; vencidos: number };
  rows: Array<{
    user_id: number;
    full_name: string;
    area: string;
    position?: string | null;
    due_date?: string | null;
    progress_pct: number;
    status: EstadoEnElEquipo | string;
  }>;
}

export interface PreguntaGuardada extends PreguntaDelQuiz {
  /** Solo en `/quiz/manage`, que exige gestión. */
  correct_key?: string | null;
}

export interface CuerpoDeLeccion {
  title: string;
  description: string | null;
  duration_minutes: number | null;
  order: number;
  video_type?: TipoDeLeccion;
  video_url?: string | null;
}

export const clavesDelDetalle = {
  curso: (id: number) => ['formacion', 'curso', id] as const,
  quiz: (id: number) => ['formacion', 'curso', id, 'quiz'] as const,
  quizGuardado: (id: number) => ['formacion', 'curso', id, 'quiz-guardado'] as const,
  asignaciones: (id: number) => ['formacion', 'curso', id, 'asignaciones'] as const,
  equipo: (id: number) => ['formacion', 'curso', id, 'equipo'] as const,
  certificados: ['formacion', 'certificados'] as const,
  areas: ['formacion', 'areas'] as const,
};

export function useCurso(id: number | null) {
  return useQuery({
    queryKey: clavesDelDetalle.curso(id ?? 0),
    queryFn: () => api.get<CursoDetalle>(`/courses/${id}`),
    enabled: id != null,
  });
}

/**
 * Los certificados de quien mira.
 *
 * El vanilla los pedía **al pulsar** «Descargar certificado» y abría el PDF con
 * `window.open` después del `await`. Un navegador con bloqueador de ventanas lo
 * bloquea en ese punto —ya no es la pulsación la que abre la ventana, es una
 * promesa que se resolvió después— y el botón no hacía nada, sin decirlo. Aquí
 * se piden antes, así que el botón es un enlace de verdad a una URL que ya
 * existe.
 */
export function useCertificados(habilitado: boolean) {
  return useQuery({
    queryKey: clavesDelDetalle.certificados,
    queryFn: () => api.get<CertificadoPropio[]>('/certificates/my'),
    enabled: habilitado,
  });
}

export function useQuiz(id: number, habilitado: boolean) {
  return useQuery({
    queryKey: clavesDelDetalle.quiz(id),
    queryFn: () => api.get<PreguntaDelQuiz[]>(`/courses/${id}/quiz`),
    enabled: habilitado,
    // Cada intento es un intento: sin caché, que el enunciado puede haber cambiado.
    staleTime: 0,
    gcTime: 0,
  });
}

function useRefrescarCurso() {
  const qc = useQueryClient();
  return (id: number) => {
    void qc.invalidateQueries({ queryKey: clavesDelDetalle.curso(id) });
    // El catálogo enseña el avance de cada curso: también cambia.
    void qc.invalidateQueries({ queryKey: claves.cursos });
  };
}

export function useInscribirse() {
  const refrescar = useRefrescarCurso();
  return useMutation({
    mutationFn: (id: number) => api.post<{ message?: string }>(`/courses/${id}/enroll`, null),
    onSuccess: (_d, id) => refrescar(id),
  });
}

export function useRecertificarse() {
  const refrescar = useRefrescarCurso();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ message?: string }>(`/courses/${id}/retake`, null),
    onSuccess: (_d, id) => {
      refrescar(id);
      void qc.invalidateQueries({ queryKey: clavesDelDetalle.certificados });
    },
  });
}

export function useCompletarLeccion() {
  const refrescar = useRefrescarCurso();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cursoId: number; leccionId: number }) =>
      api.post<RespuestaDeCompletar>(`/courses/${v.cursoId}/lessons/${v.leccionId}/complete`, null),
    onSuccess: (r, v) => {
      refrescar(v.cursoId);
      if (r.certificate_issued) void qc.invalidateQueries({ queryKey: clavesDelDetalle.certificados });
    },
  });
}

export function usePresentarQuiz() {
  const refrescar = useRefrescarCurso();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cursoId: number; respuestas: Array<{ question_id: number; selected_key: string }> }) =>
      api.post<ResultadoDelQuiz>(`/courses/${v.cursoId}/quiz/submit`, v.respuestas),
    onSuccess: (r, v) => {
      refrescar(v.cursoId);
      if (r.passed) void qc.invalidateQueries({ queryKey: clavesDelDetalle.certificados });
    },
  });
}

export function useOpinar() {
  return useMutation({
    mutationFn: (v: {
      cursoId: number; rating: number; comment: string | null;
      publish_to_feed: boolean; send_to_email: boolean;
    }) => {
      const { cursoId, ...cuerpo } = v;
      return api.post<{ message?: string }>(`/courses/${cursoId}/feedback`, cuerpo);
    },
  });
}

/** Reportar un problema. Multipart porque la evidencia es opcional; la
 *  descripción iba en la URL hasta el 2026-09-03 y un reporte largo se cortaba
 *  con un 414. */
export function useReportarProblema() {
  return useMutation({
    mutationFn: (v: { cursoId: number; descripcion: string; leccionId: number | null; archivo: File | null }) => {
      const fd = new FormData();
      fd.append('description', v.descripcion);
      if (v.leccionId) fd.append('lesson_id', String(v.leccionId));
      if (v.archivo) fd.append('file', v.archivo);
      return api.upload<{ message?: string }>(`/courses/${v.cursoId}/reports`, fd);
    },
  });
}

/* ── Gestión: lecturas ────────────────────────────────────────────────── */

export function useQuizGuardado(id: number, habilitado: boolean) {
  return useQuery({
    queryKey: clavesDelDetalle.quizGuardado(id),
    queryFn: () => api.get<PreguntaGuardada[]>(`/courses/${id}/quiz/manage`),
    enabled: habilitado,
  });
}

export function useAsignaciones(id: number, habilitado: boolean) {
  return useQuery({
    queryKey: clavesDelDetalle.asignaciones(id),
    queryFn: () => api.get<Asignacion[]>(`/courses/${id}/assignments`),
    enabled: habilitado,
  });
}

export function useProgresoDelEquipo(id: number, habilitado: boolean) {
  return useQuery({
    queryKey: clavesDelDetalle.equipo(id),
    queryFn: () => api.get<ProgresoDelEquipo>(`/courses/${id}/team-progress`),
    enabled: habilitado,
  });
}

export function useAreasDeGestion(habilitado: boolean) {
  return useQuery({
    queryKey: clavesDelDetalle.areas,
    queryFn: () => api.get<Array<{ id: number; name: string }>>('/admin/areas/'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

/* ── Gestión: escrituras ──────────────────────────────────────────────── */

export function useEstadoEditorial() {
  const refrescar = useRefrescarCurso();
  return useMutation({
    mutationFn: (v: { cursoId: number; status: EstadoEditorial }) =>
      api.put<Curso>(`/courses/${v.cursoId}`, { status: v.status }),
    onSuccess: (_d, v) => refrescar(v.cursoId),
  });
}

export function useGuardarIcono() {
  const refrescar = useRefrescarCurso();
  return useMutation({
    mutationFn: (v: { cursoId: number; cover_icon: string; cover_icon_color: string }) =>
      api.put<Curso>(`/courses/${v.cursoId}`, { cover_icon: v.cover_icon, cover_icon_color: v.cover_icon_color }),
    onSuccess: (_d, v) => refrescar(v.cursoId),
  });
}

export function useSubirPortada() {
  const refrescar = useRefrescarCurso();
  return useMutation({
    mutationFn: (v: { cursoId: number; archivo: File }) => {
      const fd = new FormData();
      fd.append('file', v.archivo);
      return api.upload<{ cover_url?: string }>(`/courses/${v.cursoId}/cover`, fd);
    },
    onSuccess: (_d, v) => refrescar(v.cursoId),
  });
}

/**
 * Crear o editar una lección, y subir su PDF si lo trae.
 *
 * Si el PDF falla, la lección YA existe: se devuelve el fallo aparte en vez de
 * tumbarla, para que el formulario diga «se guardó, pero el PDF no» y no invite
 * a guardarla otra vez —que crearía una segunda lección con el mismo título—.
 */
export function useGuardarLeccion() {
  const refrescar = useRefrescarCurso();
  return useMutation({
    mutationFn: async (v: { cursoId: number; leccionId: number | null; cuerpo: CuerpoDeLeccion; pdf: File | null }) => {
      const r = v.leccionId
        ? await api.put<{ id?: number }>(`/courses/${v.cursoId}/lessons/${v.leccionId}`, v.cuerpo)
        : await api.post<{ id: number }>(`/courses/${v.cursoId}/lessons`, v.cuerpo);
      const id = v.leccionId ?? r.id;
      let errorDelPdf: unknown = null;
      if (v.pdf && id) {
        const fd = new FormData();
        fd.append('file', v.pdf);
        try {
          await api.upload(`/courses/${v.cursoId}/lessons/${id}/pdf`, fd);
        } catch (err) {
          errorDelPdf = err;
        }
      }
      return { id, errorDelPdf };
    },
    onSuccess: (_d, v) => refrescar(v.cursoId),
  });
}

export function useBorrarLeccion() {
  const refrescar = useRefrescarCurso();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cursoId: number; leccionId: number }) =>
      api.del<{ message?: string }>(`/courses/${v.cursoId}/lessons/${v.leccionId}`),
    onSuccess: (_d, v) => {
      refrescar(v.cursoId);
      // Borrar una lección retira sus preguntas del quiz.
      void qc.invalidateQueries({ queryKey: clavesDelDetalle.quizGuardado(v.cursoId) });
    },
  });
}

/** Qué quedó guardado. `hechas` son las posiciones de `preguntas` que el servidor
 *  aceptó; si `fallo` no es nulo, las demás no llegaron a guardarse. */
export interface PreguntasGuardadas {
  hechas: number[];
  fallo: unknown;
}

/**
 * Guardar varias preguntas, una petición por pregunta.
 *
 * Si una falla a mitad, las anteriores YA existen. Contarlo como un fallo entero
 * —toast rojo, los borradores intactos y la lista de guardadas sin refrescar—
 * invitaba a pulsar «Guardar» otra vez y dejaba cada pregunta ya guardada dos
 * veces en el quiz, contando doble en la nota. Se devuelve qué se guardó y el
 * fallo aparte, como en las asignaciones, y la lista se refresca pase lo que
 * pase.
 */
export function useGuardarPreguntas() {
  const refrescar = useRefrescarCurso();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      cursoId: number; leccionId: number;
      preguntas: Array<{ question_text: string; correct_key: string; options: Array<{ key: string; text: string }> }>;
    }): Promise<PreguntasGuardadas> => {
      const hechas: number[] = [];
      for (const [i, p] of v.preguntas.entries()) {
        try {
          await api.post(`/courses/${v.cursoId}/quiz/questions`, { lesson_id: v.leccionId, ...p });
          hechas.push(i);
        } catch (err) {
          // Si no se guardó ninguna, es un fallo sin más.
          if (!hechas.length) throw err;
          return { hechas, fallo: err };
        }
      }
      return { hechas, fallo: null };
    },
    onSettled: (_d, _e, v) => {
      refrescar(v.cursoId);
      void qc.invalidateQueries({ queryKey: clavesDelDetalle.quizGuardado(v.cursoId) });
    },
  });
}

export function useBorrarPregunta() {
  const qc = useQueryClient();
  const refrescar = useRefrescarCurso();
  return useMutation({
    mutationFn: (v: { cursoId: number; preguntaId: number }) =>
      api.del<{ message?: string }>(`/courses/${v.cursoId}/quiz/questions/${v.preguntaId}`),
    onSuccess: (_d, v) => {
      refrescar(v.cursoId);
      void qc.invalidateQueries({ queryKey: clavesDelDetalle.quizGuardado(v.cursoId) });
    },
  });
}

/** Qué quedó creado al asignar. `hechas` son las posiciones de `cuerpos` que el
 *  servidor aceptó; si `fallo` no es nulo, las demás no llegaron a crearse. */
export interface AsignacionCreada {
  total: number;
  hechas: number[];
  fallo: unknown;
}

/**
 * Una asignación por cada persona elegida, igual que el vanilla: el servidor
 * acepta `user_id` de uno en uno.
 *
 * Si una falla a mitad, las anteriores YA existen. El vanilla lo contaba como
 * un fallo entero —un toast de error, la lista sin refrescar y las personas
 * todavía elegidas— y reintentar creaba otra vez las que ya estaban, porque el
 * servidor no rechaza una asignación repetida. Aquí se devuelve qué se creó y
 * el fallo aparte, como el PDF de una lección, y la lista se refresca en
 * cualquier caso.
 */
export function useCrearAsignacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { cursoId: number; cuerpos: CuerpoDeAsignacion[] }): Promise<AsignacionCreada> => {
      let total = 0;
      const hechas: number[] = [];
      for (const [i, c] of v.cuerpos.entries()) {
        try {
          const r = await api.post<{ target_count?: number }>(`/courses/${v.cursoId}/assignments`, c);
          total += r.target_count ?? 0;
          hechas.push(i);
        } catch (err) {
          // Si no se creó ninguna, es un fallo sin más.
          if (!hechas.length) throw err;
          return { total, hechas, fallo: err };
        }
      }
      return { total, hechas, fallo: null };
    },
    onSettled: (_d, _e, v) => {
      void qc.invalidateQueries({ queryKey: clavesDelDetalle.asignaciones(v.cursoId) });
      void qc.invalidateQueries({ queryKey: clavesDelDetalle.equipo(v.cursoId) });
    },
  });
}

export function useBorrarAsignacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cursoId: number; asignacionId: number }) =>
      api.del<{ message?: string }>(`/courses/${v.cursoId}/assignments/${v.asignacionId}`),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: clavesDelDetalle.asignaciones(v.cursoId) });
      void qc.invalidateQueries({ queryKey: clavesDelDetalle.equipo(v.cursoId) });
    },
  });
}

export function useRecordarAsignacion() {
  return useMutation({
    mutationFn: (v: { cursoId: number; asignacionId: number }) =>
      api.post<{ message: string }>(`/courses/${v.cursoId}/assignments/${v.asignacionId}/remind`, null),
  });
}

export function useReiniciarProgreso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cursoId: number; userId: number }) =>
      api.post<{ message?: string }>(`/admin/courses/${v.cursoId}/reset/${v.userId}`, null),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: clavesDelDetalle.equipo(v.cursoId) }),
  });
}
